// Item 411 Phase F: per-repo release-hold override on the repo detail page
// (remote repos only). Reads/writes the repo-scoped rule of the admin
// release-holds API; "0" switches the hold off for this repo, "clear" falls
// back to the project/global rule. Hidden when the API refuses (non-admin).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useReleaseHolds, putReleaseHold, deleteReleaseHold } from '../api/pullGate';

export function RepoReleaseHoldCard({ repoID }: { readonly repoID: string }) {
  const { t } = useTranslation();
  const { data, error, mutate } = useReleaseHolds();
  const [hours, setHours] = useState('');
  const [err, setErr] = useState('');

  if (error || data === undefined) return null;
  const rule = data.find((r) => r.scope_kind === 'repo' && r.scope_id === repoID && !r.format);

  const n = Number(hours);
  const hoursOk = hours.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= 720;

  const run = async (fn: () => Promise<unknown>) => {
    setErr('');
    try {
      await fn();
      await mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader title={t('repoDetail.releaseHold.title')} description={t('repoDetail.releaseHold.description')} />
      <CardBody>
        {err && <p className="text-sm text-danger mb-3">{err}</p>}
        <p className="text-sm mb-3">
          {rule
            ? rule.hold_hours === 0
              ? t('repoDetail.releaseHold.overrideOff')
              : t('repoDetail.releaseHold.override', { count: rule.hold_hours })
            : t('repoDetail.releaseHold.inherited')}
        </p>
        <div className="flex items-center gap-2">
          <Input
            aria-label={t('repoDetail.releaseHold.hours')}
            placeholder={t('repoDetail.releaseHold.hours')}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
          <Button
            disabled={!hoursOk}
            onClick={() =>
              run(() =>
                putReleaseHold({
                  scope_kind: 'repo',
                  scope_id: repoID,
                  format: '',
                  hold_hours: n,
                  security_fast_track: rule?.security_fast_track ?? true,
                }),
              )
            }
          >
            {t('repoDetail.releaseHold.save')}
          </Button>
          {rule && (
            <Button onClick={() => run(() => deleteReleaseHold(rule.id))}>{t('repoDetail.releaseHold.clear')}</Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
